import React, { useCallback, useState } from 'react'
import { ProjectCard } from '../components/projects/ProjectCard'
import { ProjectFilters } from '../components/projects/ProjectFilters'
import { SEOHead } from '../components/seo/SEOHead'
import { Button, Card, Container, Heading, Text } from '../components/ui'
import { useProjects } from '../hooks/useProjects'
import '../styles/ProjectsPage.css'

// Portfolio statistics constants
const PORTFOLIO_STATS = {
  experience: '15+ Years of Software Architecture',
  games: '22 Shipped Games',
  applications: '14 Applications',
  platforms: 8,
  usersServed: 'Millions',
  yearsExperience: '15+',
} as const

// Empty state messages
const EMPTY_STATE_MESSAGES = {
  title: 'No projects found matching the selected filters.',
  subtitle: 'Try adjusting your filters or ',
  resetAction: 'view all projects',
} as const

/**
 * Projects page component showcasing Lucas Stufflebeam's comprehensive project portfolio
 * across game development, enterprise systems, mobile applications, and web development.
 *
 * Features:
 * - Categorized project display with filtering
 * - Detailed project descriptions with technical stacks
 * - Achievement metrics and impact statements
 * - Responsive grid layout with search functionality
 * - Links to live projects where available
 * - Performance optimized with memoization
 * - Accessible with ARIA labels and keyboard navigation
 * - SEO optimization for technical keyword discovery
 */

const ProjectsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [showFeaturedOnly, setShowFeaturedOnly] = useState<boolean>(false)

  const { filteredProjects, stats, isEmpty } = useProjects({
    selectedCategory,
    showFeaturedOnly,
  })

  // Memoized event handlers for performance
  const handleCategoryChange = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId)
  }, [])

  const handleFeaturedToggle = useCallback((featured: boolean) => {
    setShowFeaturedOnly(featured)
  }, [])

  const handleResetFilters = useCallback(() => {
    setSelectedCategory('all')
    setShowFeaturedOnly(false)
  }, [])

  return (
    <>
      {/* SEO Meta Tags for Projects Page */}
      <SEOHead page="projects" path="/projects" />

      <main className="page-main" role="main">
        <Container size="lg" padding="lg">
          {/* Page Header */}
          <header className="projects-header">
            <Heading as="h1" size="3xl" variant="gradient">
              Project Portfolio
            </Heading>
            <Text size="lg" variant="secondary">
              {PORTFOLIO_STATS.experience} • {PORTFOLIO_STATS.games} •{' '}
              {PORTFOLIO_STATS.applications}
            </Text>

            {/* Portfolio Stats */}
            <div
              className="projects-stats"
              role="region"
              aria-label="Portfolio statistics"
            >
              <Card variant="glass" padding="sm" className="stat-card">
                <span className="stat-number">{stats.totalProjects}+</span>
                <Text size="sm" variant="tertiary">
                  Total Projects
                </Text>
              </Card>
              <Card variant="glass" padding="sm" className="stat-card">
                <span className="stat-number">{PORTFOLIO_STATS.platforms}</span>
                <Text size="sm" variant="tertiary">
                  Platforms
                </Text>
              </Card>
              <Card variant="glass" padding="sm" className="stat-card">
                <span className="stat-number">
                  {PORTFOLIO_STATS.usersServed}
                </span>
                <Text size="sm" variant="tertiary">
                  Users Served
                </Text>
              </Card>
              <Card variant="glass" padding="sm" className="stat-card">
                <span className="stat-number">
                  {PORTFOLIO_STATS.yearsExperience}
                </span>
                <Text size="sm" variant="tertiary">
                  Years Experience
                </Text>
              </Card>
            </div>
          </header>

          {/* Filters */}
          <ProjectFilters
            selectedCategory={selectedCategory}
            showFeaturedOnly={showFeaturedOnly}
            onCategoryChange={handleCategoryChange}
            onFeaturedToggle={handleFeaturedToggle}
          />

          {/* Projects Grid */}
          <section
            className="projects-grid"
            role="region"
            aria-label={`${filteredProjects.length} projects ${showFeaturedOnly ? '(featured only)' : ''}`}
          >
            {filteredProjects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </section>

          {/* Empty State */}
          {isEmpty && (
            <Card
              variant="surface"
              padding="lg"
              className="projects-empty-state"
              role="status"
              aria-live="polite"
            >
              <div className="empty-state-content">
                <Text size="lg" variant="secondary">
                  {EMPTY_STATE_MESSAGES.title}
                </Text>
                <Text
                  size="base"
                  variant="tertiary"
                  className="empty-state-subtitle"
                >
                  {EMPTY_STATE_MESSAGES.subtitle}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetFilters}
                    className="empty-state-button"
                  >
                    {EMPTY_STATE_MESSAGES.resetAction}
                  </Button>
                  .
                </Text>
              </div>
            </Card>
          )}
        </Container>
      </main>
    </>
  )
}

export default ProjectsPage
